// @flow

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { type GatsbyImageData, StaticImage } from 'gatsby-plugin-image'
import * as React from 'react'
import Helmet from 'react-helmet'

import { animations, colors, media, variables } from '@toggl/style'

import { useAutoPlayRef } from '../hooks/useAutoPlayRef'
import * as Responsive from '../responsive'
import * as text from '../text'

const getPosterUrl = (imageData: ?GatsbyImageData) =>
  imageData?.images.sources[0].srcSet.split(',')[3].split(' ')[0].trim()

type Props = {
  skewed?: boolean,
  bgImage?: React.Element<typeof StaticImage> | React.Element<'img'>,
  bgVideo?: string,
  children: React.Node,
  solidColor?: string,
  ...
}
export const Wrapper = ({
  bgImage,
  bgVideo,
  skewed,
  children,
  solidColor,
  ...props
}: Props) => {
  const shouldAutoPlay = useAutoPlayRef()
  const poster = bgImage?.props.__imageData
    ? getPosterUrl(bgImage.props.__imageData)
    : bgImage?.props.src

  return (
    <Root {...props} solidColor={solidColor} skewed={skewed}>
      {bgVideo && (
        <Helmet>
          {bgVideo && (
            <link
              rel="preload"
              href={bgVideo}
              as="video"
              media={media.mqbp[1]}
            />
          )}
          {poster && <link rel="preload" href={poster} as="image" />}
        </Helmet>
      )}

      {bgVideo ? (
        <>
          <Responsive.DesktopOnly>
            <VideoWrapper skewed={skewed}>
              <VideoStyled
                muted
                loop
                skewed={skewed}
                poster={poster}
                ref={shouldAutoPlay}
              >
                <source src={bgVideo} />
              </VideoStyled>
              <Mask />
            </VideoWrapper>
          </Responsive.DesktopOnly>
          {poster ? (
            <Responsive.MobileOnly>
              <img src={poster} alt="Hero background image" />
            </Responsive.MobileOnly>
          ) : (
            bgImage && <Responsive.MobileOnly>{bgImage}</Responsive.MobileOnly>
          )}
        </>
      ) : (
        bgImage
      )}

      {children}
    </Root>
  )
}

export const Root: React.ComponentType<any> = styled.div`
  position: relative;
  min-height: ${200 - variables.headerHeight}px;
  background-color: black;

  ${(p) => (p.solidColor ? `background-color: ${p.solidColor};` : '')}

  ${media.mq[1]} {
    margin-top: -${variables.headerHeightFull}px;
  }

  > div > img {
    object-fit: cover;
  }

  > div > img,
  .gatsby-image-wrapper {
    position: absolute !important;
    width: 100%;
    height: 100%;

    ${({ skewed }) =>
      skewed &&
      css`
        transform: rotate(10deg);
        height: calc(100% - 35px);
      `}
  }

  > div > img,
  .gatsby-image-wrapper img {
    mask-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5) 100%);
  }
`

const VideoWrapper: React.ComponentType<any> = styled.div`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;

  ${({ skewed }) =>
    skewed && 'animation: spinner 1s; height: calc(100% - 150px);'}
  animation-fill-mode: forwards;
  animation-delay: 0.4s;

  @keyframes spinner {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(10deg);
    }
  }

  ${media.mq[3]} {
    ${({ skewed }) => skewed && 'height: calc(100% - 250px);'}
  }
`

const Mask = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
`

const VideoStyled = styled.video`
  position: absolute;
  min-width: 100%;
  min-height: 100%;
`

export const ContentWrapper: React.ComponentType<any> = styled.div`
  position: relative;
  padding: 30px;

  ${(p) =>
    p.center
      ? `p {
    text-align: center;
  }`
      : ''}

  > div:nth-child(1) {
    animation: 300ms ease-in-out ${animations.cardUp};
  }

  > div:nth-child(2) {
    animation: 500ms ease-in-out ${animations.cardUp};
  }

  > div:nth-child(3) {
    animation: 700ms ease-in-out ${animations.cardUp};
  }

  > div:nth-child(4) {
    animation: 900ms ease-in-out ${animations.cardUp};
  }

  ${media.mq[1]} {
    margin: ${variables.headerHeightFull}px auto 0;
    max-width: 1200px;
    padding-bottom: 60px;
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 20px;
  }
`

export const Title: React.ComponentType<any> = styled.h1`
  ${text.heading3};
  color: ${colors.almostWhite};
  margin-bottom: 25px;
  text-align: ${(p) => (p.centered ? 'center' : 'left')};

  ${media.mq[1]} {
    ${text.heading1};
  }

  em {
    color: ${(p) => p.theme.primary};
  }
`

export const Text: React.ComponentType<any> = styled.div`
  ${text.heading5};
  color: ${colors.beige};
  margin-top: 10px;
  margin-bottom: 25px;
  width: 100%;
`

export const CtaSection: React.ComponentType<any> = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  button,
  a {
    margin-top: 15px;
  }
  ${media.mq[1]} {
    flex-direction: row;
    a:not(:first-child) {
      margin-left: 25px;
    }
  }
`

export const LoginSmallTitle: React.ComponentType<any> = styled.div`
  ${text.paragraph2};
  color: ${colors.almostWhite};
  text-align: center;
  margin-bottom: 15px;

  ${media.mq[1]} {
    margin-bottom: 30px;
  }
`

export const LoginWrapper: React.ComponentType<any> = styled(ContentWrapper)`
  ${media.mq[1]} {
    padding: 80px 0;
  }
`

export const TitleBig: React.ComponentType<any> = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  ${media.mq[1]} {
    margin: 100px auto 80px auto;
    h1 {
      ${text.heading1};
      color: ${colors.almostWhite};
    }
  }

  p {
    margin-top: 20px;
    color: ${colors.almostWhite};
    ${text.paragraph2}
  }
`
